import type { Outing } from '../types';
import type { PricingConfig } from './pricingEngine';

type PricingDetailLine = {
  label: string;
  value: string;
};

type PricingDisplaySummary = {
  homePrimaryLabel: string | null;
  homeBadges: string[];
  detailLines: PricingDetailLine[];
};

function formatEuro(amount: number): string {
  return `${amount} €`;
}

export function getStartingPrice(pricingConfig?: PricingConfig | null): number | null {
  if (!pricingConfig) {
    return null;
  }

  const positiveRates = [
    pricingConfig.normalRate,
    ...pricingConfig.specialRates.filter((rate) => rate.enabled),
  ]
    .map((rate) => Number(rate.unitPrice))
    .filter((rate) => Number.isFinite(rate) && rate > 0);

  if (positiveRates.length > 0) {
    return Math.min(...positiveRates);
  }

  return pricingConfig.freeChildRule.enabled ? 0 : null;
}

export function getOutingPricingDisplay(outing: Pick<Outing, 'pricingConfig'>): PricingDisplaySummary {
  const pricingConfig = outing.pricingConfig;
  const startingPrice = getStartingPrice(pricingConfig);

  if (!pricingConfig) {
    return {
      homePrimaryLabel: null,
      homeBadges: [],
      detailLines: [],
    };
  }

  const activeSpecialRates = pricingConfig.specialRates.filter((rate) => rate.enabled);
  const homeBadges: string[] = [];

  if (pricingConfig.freeChildRule.enabled) {
    homeBadges.push(`Enfants < ${pricingConfig.freeChildRule.maxAgeExclusive} ans gratuits`);
  }

  if (activeSpecialRates.length > 0) {
    homeBadges.push('Tarifs spéciaux disponibles');
  }

  return {
    homePrimaryLabel: startingPrice === null
      ? null
      : startingPrice > 0
        ? `À partir de ${formatEuro(startingPrice)}`
        : 'Gratuit',
    homeBadges,
    detailLines: [
      {
        label: 'Tarif normal',
        value: formatEuro(pricingConfig.normalRate.unitPrice),
      },
      ...(pricingConfig.freeChildRule.enabled
        ? [{
            label: `Enfants de moins de ${pricingConfig.freeChildRule.maxAgeExclusive} ans`,
            value: 'Gratuit',
          }]
        : []),
      ...activeSpecialRates.map((rate) => ({
        label: rate.label,
        value: formatEuro(rate.unitPrice),
      })),
    ],
  };
}